// N-15b: 通知先名簿の一覧（名前 / メール / LINE / 4 種類 × メール・LINE / 有効）。
// S-136: v2 の ZebraTable + Chip。ゼブラ行の上のボタンは白面とも灰面とも同色になりうるので、
// 編集 = ブランド色の面 / 削除 = 面なし（文字だけ危険色）の生クラス（memory S-135 の判断）。

import {
  ADMIN_NOTIFICATION_KINDS,
  ADMIN_NOTIFICATION_KIND_LABELS,
  type NotificationRecipient,
  type NotificationRecipientsUpsertWarning,
} from '@kawai/shared';
import { Chip, ZebraTable } from '@kawai/ui';

export interface RecipientsTableProps {
  recipients: ReadonlyArray<NotificationRecipient>;
  /** 直近の保存で返った warning（recipientId で突合）。 */
  warnings?: ReadonlyArray<NotificationRecipientsUpsertWarning>;
  canEdit: boolean;
  onEdit: (r: NotificationRecipient) => void;
  onRemove: (r: NotificationRecipient) => void;
}

function flagsCell(r: NotificationRecipient, kind: (typeof ADMIN_NOTIFICATION_KINDS)[number]) {
  const f = r.kinds[kind];
  const parts = [f.email ? 'メール' : null, f.line ? 'LINE' : null].filter(Boolean);
  return parts.length ? parts.join(' / ') : '—';
}

export function RecipientsTable({
  recipients,
  warnings = [],
  canEdit,
  onEdit,
  onRemove,
}: RecipientsTableProps) {
  if (recipients.length === 0) {
    return (
      <p className="text-body text-ink-3" data-testid="recipients-empty">
        通知先はまだ登録されていません。
      </p>
    );
  }
  const warnById = new Map(warnings.map((w) => [w.recipientId, w]));
  return (
    <div className="overflow-x-auto">
      <ZebraTable className="w-full" data-testid="recipients-table" aria-label="通知先の名簿">
        <thead>
          <ZebraTable.HeadRow>
            <ZebraTable.Th edge="first">名前</ZebraTable.Th>
            <ZebraTable.Th>メール</ZebraTable.Th>
            <ZebraTable.Th>LINE</ZebraTable.Th>
            {ADMIN_NOTIFICATION_KINDS.map((k) => (
              <ZebraTable.Th key={k} className="whitespace-nowrap">
                {ADMIN_NOTIFICATION_KIND_LABELS[k]}
              </ZebraTable.Th>
            ))}
            <ZebraTable.Th edge={canEdit ? undefined : 'last'}>状態</ZebraTable.Th>
            {canEdit && <ZebraTable.Th edge="last" />}
          </ZebraTable.HeadRow>
        </thead>
        <tbody>
          {recipients.map((r) => {
            const warn = warnById.get(r.id);
            return (
              <ZebraTable.Row key={r.id} className="align-top">
                <ZebraTable.Td className="font-medium whitespace-nowrap">{r.name}</ZebraTable.Td>
                <ZebraTable.Td className="break-all">{r.email ?? '—'}</ZebraTable.Td>
                <ZebraTable.Td>
                  {r.lineUserId ? (
                    <span>
                      {r.lineDisplayName ?? r.lineUserId}
                      {warn && (
                        <Chip tone="warning" className="ml-1">
                          届きません
                        </Chip>
                      )}
                    </span>
                  ) : (
                    '—'
                  )}
                </ZebraTable.Td>
                {ADMIN_NOTIFICATION_KINDS.map((k) => (
                  <ZebraTable.Td key={k} className="whitespace-nowrap">
                    {flagsCell(r, k)}
                  </ZebraTable.Td>
                ))}
                <ZebraTable.Td className="whitespace-nowrap">
                  {r.enabled ? <Chip tone="success">有効</Chip> : <Chip tone="neutral">無効</Chip>}
                </ZebraTable.Td>
                {canEdit && (
                  <ZebraTable.Td className="whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => onEdit(r)}
                      className="rounded-lg bg-brand-tint px-3.5 py-1.5 text-label font-medium text-brand-ink hover:opacity-90"
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemove(r)}
                      className="ml-1 rounded-lg px-3.5 py-1.5 text-label font-medium text-danger-ink hover:bg-surface-3"
                    >
                      削除
                    </button>
                  </ZebraTable.Td>
                )}
              </ZebraTable.Row>
            );
          })}
        </tbody>
      </ZebraTable>
    </div>
  );
}
